import React, { useEffect, useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const CourseReviewForm = ({ courseId, token, courseTitle }) => {
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [content, setContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API_URL}/api/courses/${courseId}/reviews/mine`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => setMine(j?.review || null))
      .catch(() => setMine(null))
      .finally(() => setLoading(false));
  }, [courseId, token]);

  const submit = async () => {
    if (rating < 1) { toast.error('Pick a star rating first'); return; }
    if (content.trim().length < 10) { toast.error('Review must be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/${courseId}/reviews`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating, content: content.trim() }),
      });
      const j = await res.json();
      if (!res.ok) throw new Error(j.detail || 'Submit failed');
      toast.success('Thanks! Your review is pending approval.');
      setMine(j.review);
      setOpen(false);
    } catch (e) {
      toast.error(e.message || 'Could not submit review');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return null;

  // Already submitted — show status banner
  if (mine) {
    const status = mine.status || 'pending';
    const colorMap = {
      pending: 'border-amber-500/30 bg-amber-500/10 text-amber-300',
      approved: 'border-green-500/30 bg-green-500/10 text-green-300',
      rejected: 'border-red-500/30 bg-red-500/10 text-red-300',
    };
    const statusText = {
      pending: '⏳ Pending admin approval',
      approved: '✓ Published on course page',
      rejected: '✗ Not approved',
    };
    return (
      <div className={`px-4 py-3 border-t flex items-center justify-between gap-3 ${colorMap[status] || colorMap.pending}`} data-testid="course-review-status">
        <div className="flex items-center gap-2 text-sm">
          <div className="flex">
            {[1, 2, 3, 4, 5].map((n) => (
              <Star key={n} className={`w-4 h-4 ${n <= mine.rating ? 'fill-current' : 'opacity-30'}`} strokeWidth={1.5} />
            ))}
          </div>
          <span>Your review · {statusText[status]}</span>
        </div>
      </div>
    );
  }

  // Form trigger / open form
  if (!open) {
    return (
      <div className="px-4 py-3 border-t border-golden/20 bg-black/40 flex items-center justify-between" data-testid="course-review-prompt">
        <div className="text-sm">
          <p className="text-white font-medium">Enjoyed this course?</p>
          <p className="text-gray-400 text-xs">Share a quick rating to help fellow traders.</p>
        </div>
        <Button
          size="sm"
          onClick={() => setOpen(true)}
          className="bg-golden text-black hover:bg-golden/90"
          data-testid="open-review-form-btn"
        >
          Rate course
        </Button>
      </div>
    );
  }

  return (
    <div className="px-4 py-4 border-t border-golden/20 bg-black/60 space-y-3" data-testid="course-review-form">
      <div>
        <p className="text-white font-medium text-sm mb-1">Rate "{courseTitle}"</p>
        <p className="text-gray-500 text-xs">Your review will be visible after admin approval.</p>
      </div>
      <div className="flex items-center gap-1" data-testid="course-review-stars">
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            onClick={() => setRating(n)}
            className="p-1"
            data-testid={`review-star-${n}`}
          >
            <Star
              className={`w-6 h-6 transition-colors ${
                (hover || rating) >= n ? 'fill-amber-400 text-amber-400' : 'text-gray-600'
              }`}
              strokeWidth={1.5}
            />
          </button>
        ))}
        {rating > 0 && <span className="text-xs text-gray-400 ml-2">{rating}/5</span>}
      </div>
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        placeholder="What did you like? What could be improved? (min 10 characters)"
        maxLength={1000}
        rows={4}
        className="w-full px-3 py-2 bg-white/5 border border-golden/30 rounded-lg text-white text-sm placeholder-gray-500 focus:outline-none focus:border-golden resize-none"
        data-testid="review-content-input"
      />
      <div className="flex items-center justify-between text-xs text-gray-500">
        <span>{content.length}/1000</span>
        <div className="flex gap-2">
          <Button size="sm" variant="ghost" onClick={() => setOpen(false)} className="text-gray-400 hover:text-white">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={submit}
            disabled={submitting}
            className="bg-golden text-black hover:bg-golden/90"
            data-testid="submit-review-btn"
          >
            {submitting ? 'Submitting...' : 'Submit review'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CourseReviewForm;
